import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Grid2, MenuItem, TextField } from '@mui/material';
import { UseOrganizationMember } from 'features/users/hooks/useOrganizationMember';
import BlackButton from 'components/BlackButton';
import { OrganizationMember, UserPlanType } from 'features/users/users.types';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { getShallowDiff } from 'shared/utils';
import * as Yup from 'yup';
import { enqueueSnackbar } from 'notistack';
import { useAuthState } from 'state/useAuthState';
import { logUnexpectedErrorAndNotifyUserWithTryAgain } from 'shared/handleUnexpectedError';

type OrganizationMemberProps = {
  member?: OrganizationMember;
  saveMember: UseOrganizationMember['saveMember'];
  updateMember: UseOrganizationMember['updateMember'];
  deleteMember: UseOrganizationMember['deleteMember'];
};

type FormValues = {
  firstName: string;
  lastName: string;
  email: string;
  planType: UserPlanType | null;
};

export const EditOrganizationMemberForm = (
  props: OrganizationMemberProps,
): JSX.Element => {
  const { member, saveMember, updateMember, deleteMember } = props;
  const { t } = useTranslation();
  const history = useHistory();
  const { user } = useAuthState();

  const { control, formState, handleSubmit, setError } = useForm<FormValues>({
    resolver: yupResolver(
      Yup.object().shape({
        firstName: Yup.string().required(
          t('organizationMember.inputs.firstName.required'),
        ),
        lastName: Yup.string().required(
          t('organizationMember.inputs.lastName.required'),
        ),
        email: Yup.string().when('$create', {
          is: true,
          then: Yup.string()
            .email(t('organizationMember.inputs.email.invalid'))
            .required(t('organizationMember.inputs.email.required')),
          otherwise: Yup.string().email().nullable(),
        }),
        planType: Yup.string().when('$create', {
          is: true,
          then: Yup.string()
            .nullable()
            .required(t('organizationMember.inputs.planType.required')),
          otherwise: Yup.string().nullable(),
        }),
      }),
    ),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      planType: null,
    },
    values: {
      firstName: member?.firstName || '',
      lastName: member?.lastName || '',
      email: member?.email || '',
      planType: member?.planType || null,
    },
    mode: 'onBlur',
    context: { create: !member },
  });

  const handleSubmitMember: SubmitHandler<FormValues> = async (values) => {
    if (!member) {
      try {
        await saveMember.mutateAsync({
          ...values,
          planType: values.planType!,
          role: 'member',
        });
        history.push('/organization');
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        if (error.response?.data?.[0] === 'user-already-exists') {
          setError('email', {
            message: t('organizationMember.inputs.email.alreadyRegistered'),
          });
          return;
        }
        logUnexpectedErrorAndNotifyUserWithTryAgain(t, { error });
      }
      return;
    }

    const { diff, isEmpty } = getShallowDiff<FormValues>(member, values);

    try {
      if (!isEmpty) {
        await updateMember.mutateAsync({
          ...diff,
          planType: diff.planType || undefined,
        });
      }
      history.push('/organization');
    } catch (error) {
      logUnexpectedErrorAndNotifyUserWithTryAgain(t, { error });
    }
  };

  const handleDeleteMember = async (): Promise<void> => {
    try {
      // eslint-disable-next-line no-restricted-globals,no-alert
      if (!confirm(t('organizationMember.deleteConfirmation'))) {
        return;
      }
      await deleteMember.mutateAsync();
      enqueueSnackbar(t('organizationMember.deleteSuccess'), {
        variant: 'success',
      });
      history.push('/organization');
    } catch (error) {
      logUnexpectedErrorAndNotifyUserWithTryAgain(t, { error });
    }
  };

  // Check if the user is self or an owner this will be enforced in the backend as well
  const canDeleteMember =
    Boolean(member) &&
    member?.role !== 'owner' &&
    member?.email !== user?.email;

  return (
    <Grid2
      container
      spacing={2}
      direction="column"
      sx={{ flex: 1, pt: 1 }}
      component="form"
      noValidate
      onSubmit={handleSubmit(handleSubmitMember)}
    >
      <Grid2 container spacing={2}>
        <Grid2 size={6}>
          <Controller
            control={control}
            name="firstName"
            render={({ field, fieldState }) => {
              const { ref, ...muiFieldProps } = field;
              return (
                <TextField
                  id="field-control-firstName"
                  label={t('organizationMember.inputs.firstName.label')}
                  fullWidth
                  {...muiFieldProps}
                  inputRef={ref}
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                  disabled={formState.isSubmitting}
                />
              );
            }}
          />
        </Grid2>
        <Grid2 size={6}>
          <Controller
            control={control}
            name="lastName"
            render={({ field, fieldState }) => {
              const { ref, ...muiFieldProps } = field;
              return (
                <TextField
                  id="field-control-lastName"
                  label={t('organizationMember.inputs.lastName.label')}
                  fullWidth
                  {...muiFieldProps}
                  inputRef={ref}
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                  disabled={formState.isSubmitting}
                />
              );
            }}
          />
        </Grid2>
      </Grid2>

      <Grid2>
        <Controller
          control={control}
          name="email"
          render={({ field, fieldState }) => {
            const { ref, ...muiFieldProps } = field;
            return (
              <TextField
                id="field-control-email"
                label={t('organizationMember.inputs.email.label')}
                fullWidth
                {...muiFieldProps}
                inputRef={ref}
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
                disabled={Boolean(member) || formState.isSubmitting}
              />
            );
          }}
        />
      </Grid2>

      <Grid2>
        <Controller
          control={control}
          name="planType"
          render={({ field, fieldState }) => {
            const { ref, value, ...muiFieldProps } = field;
            return (
              <TextField
                id="field-control-planType"
                select
                label={t('organizationMember.inputs.planType.label')}
                fullWidth
                value={value ?? ''}
                {...muiFieldProps}
                inputRef={ref}
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
                disabled={formState.isSubmitting}
              >
                <MenuItem value="regular">
                  {t('organizationMember.inputs.planType.values.regular')}
                </MenuItem>
                <MenuItem value="pro">
                  {t('organizationMember.inputs.planType.values.pro')}
                </MenuItem>
              </TextField>
            );
          }}
        />
      </Grid2>

      <Grid2
        size="grow"
        container
        direction="column"
        sx={{ justifyContent: 'flex-end' }}
      >
        {canDeleteMember && (
          <Grid2>
            <BlackButton
              fullWidth
              size="large"
              onClick={handleDeleteMember}
              loading={deleteMember.isPending}
              loadingPosition="end"
            >
              {t('organizationMember.buttons.delete')}
            </BlackButton>
          </Grid2>
        )}

        <Grid2>
          <Button
            fullWidth
            size="large"
            type="submit"
            loading={formState.isSubmitting}
            loadingPosition="end"
          >
            {t('organizationMember.buttons.save')}
          </Button>
        </Grid2>
      </Grid2>
    </Grid2>
  );
};
